/* globals globalNavigate */
import React, { useState, useEffect, useContext } from "react";
import styled from "styled-components";
import { parseSubscriberInformation } from "./api";
import { expressTarget } from "common/api";
import { TargetInput } from "./TargetContext";
import ActionsContext from "common/ActionsContext";
import Request from "common/Request";
import SelectInputWithIcon from "common/SelectInputWithIcon";
import useProfile from "common/hooks/useProfile";
import { useSearchParams } from "react-router-dom";
import { openPolicy } from "views/Status/Subscribers/Report/ListSubscribers";
import BqnContext from "common/BqnContext";

const noIPv6Mask = { ipv6Mask: false };
const returnTo = "viewSubsDetail";

const doSubcriberInformationRetrieval = (target, bqnId = null, mode = "standalone") =>
  expressTarget(target, noIPv6Mask).then((expressedTarget) =>
    ifCl.run(`show subscriber ${expressedTarget} short${bqnId !== null ? ` bqn-id ${bqnId}` : ''}`)
  )
    .then((result) => parseSubscriberInformation(result, mode))
    .catch(error => console.error("error doing show subscriber <target> short", error));

const SubscriberInformationFieldsLayout = styled.div`
  padding-left: 3rem;
  padding-right: 1rem;
  .fact {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    width: 265px;
    color: #777;
    .field {
      font-size: 1em;
      text-align: left;
    }
    .value {
      text-align: right;
      font-weight: 700;
      white-space: pre-wrap;
      white-space: -moz-pre-wrap;
      white-space: -pre-wrap;
      white-space: -o-pre-wrap;
      word-wrap: break-word;
      overflow: hidden;
    }
    &.set-1.odd {
      grid-column: 1/2;
    }
    &.set-1.even {
      grid-column: 2/3;
    }
    @media only screen and (max-width: 600px) {
      &.set-1.odd,
      &.set-1.even {
        grid-column: 1/2;
      }
    }
  }
  .grid-dense {
    grid-auto-flow: row dense;
  }
`; //

const CenteredDiv = styled.div`
  flex: 1 1 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const SubscriberInformationContextMenu = ({ doReload = doesNothing }) => {
  return (
    <ul className="context-menu header-dropdown m-r--5">
      <li>
        <a
          onClick={doReload}
          data-toggle="cardloading"
          data-loading-effect="pulse"
          title="Refresh"
        >
          <i className="material-icons">refresh</i>
        </a>
      </li>
    </ul>
  );
};
const NothingToShow = ({ targetType = "Subscriber", onLoaded, target }) => {
  useEffect(() => {
    onLoaded();
  }, [target])

  return <CenteredDiv>
    <h4>{targetType} currently not active.</h4>
  </CenteredDiv>
}

const SubscriberInformationHeaderDiv = styled.div`
  display: flex;
  flex-flow: row wrap;
  gap: 10px;
  border: none;
  padding: 0 0 1rem 1rem;
  align-items: center;
  justify-content: self-start;
  align-items: center;
  & .header-dropdown {
    position: initial;
    top: 0;
    right: 0;
    margin: 0 0 0 1rem;
  }
  @media print {
    display: none;
  }
  & .title {
    flex: 100% 1 1;
    padding-right: 2.5ch;
    text-transform: uppercase;
  }
`;

const FactsColumns = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  column-gap: 115px;
`;

const FactsRow = styled.div`
  display: flex;
  justify-content: left;
  flex-wrap: wrap;
  column-gap: 115px;
`;

const AdditionalDetailsStyled = styled.div`
  width: 145px;
  margin-top: 15px;
  margin-bottom: 10px;
`;

const doesNothing = () => {};

const ShowField = ({ field }) => {
  if (!field) {
    return <div className="fact"></div>;
  }

  return (
    <div className="fact" key={field.label}>
      <span className="field">{field.label}:</span>
      <span className="value">{field.value}</span>
    </div>
  );
};

const ShowPolicyField = ({
  field,
  type,
  openPolicy = doesNothing,
}) => {
  const profile = useProfile();
  if (!field) {
    return <div className="fact"></div>;
  }
  if (field.value === "n/a") {
    return (
      <div className="fact" key={field.label}>
        <span className="field">{field.label}:</span>
        <span className="value">n/a</span>
      </div>
    );
  }
  if(profile === null){
    return null;
  }

  return (
    <div className="fact" key={field.label}>
      <span className="field">{field.label}:</span>
      <span
        className={`value ${
          field.label !== null && profile.hideRates !== true ? "hyperlink-text" : ""
        }`}
        onClick={profile.hideRates ? null : () => openPolicy(field.value)}
      >
        {field.value}
      </span>
    </div>
  );
};

const ShowSubsGroupField = ({ field, ipAddress, onLeavingPage, target, dpiEnabled, timePeriod }) => {
  if (!field) {
    return <div className="fact"></div>;
  }
  return (
    <div className="fact" key={field.label}>
      <span className="field">{field.label}:</span>
      <span
        className="value hyperlink-text"
        onClick={() => {
          onLeavingPage();
          globalNavigate("viewStatusSubscriberGroups", {
            returnView: returnTo,
            ipAddress,
            ...target,
            dpiEnabled,
            timePeriod,
            use: target.subsId ? 'subsId' : 'ipAddress'
          });
        }}
      >
        yes
      </span>
    </div>
  );
};
const ShowSubsGroupLinkDiv = styled.div`
`
const ShowSubsGroupLink = ({ ipAddress, onLeavingPage, target, dpiEnabled, timePeriod }) => (
  ipAddress === null ? null :
  <ShowSubsGroupLinkDiv>
    <span
      className="value hyperlink-text font-bold"
      onClick={() => {
        onLeavingPage();
        globalNavigate("viewStatusSubscriberGroups", {
          returnView: returnTo,
          ipAddress,
          ...target,
          dpiEnabled,
          timePeriod,
          use: target.subsId ? 'subsId' : 'ipAddress'
        });
      }}
    >
      View subscriber groups
    </span>
  </ShowSubsGroupLinkDiv>
);

const ShowQuotaField = ({ field, ipAddress, onLeavingPage, target, dpiEnabled }) => {
  if (!field) {
    return <div className="fact"></div>;
  }
  return (
    <div className="fact" key={field.label}>
      <span className="field">{field.label}:</span>
      <span
        className={`value ${field.value === "yes" ? "hyperlink-text" : ""}`}
        onClick={() => {
          if (field.value === "no") {
            return;
          }
          onLeavingPage();
          globalNavigate("viewSubscriberQuotas", {
            returnView: returnTo,
            subnet: ipAddress,
            ...target,
            dpiEnabled,
          });
        }}
      >
        {field.value}
      </span>
    </div>
  );
};

let showAdditionalDetails = false;

const ipAddressOn = items =>
  (items.find(({label})=> label === 'IP Address') || {value: null})['value']

const ShowFacts = ({ items, onLeavingPage, target, dpiEnabled, timePeriod, onAddressLoaded=doesNothing }) => {
  useEffect(() => {
    onAddressLoaded(ipAddressOn(items))
    if(showAdditionalDetails){
      $('#subcriberInformation').collapse('show')
    }
  }, [])

  return (
    <>
      <FactsColumns>
        <FactsColumns>
          <ShowField field={items[0]} />
          <ShowField field={items[2]} />
        </FactsColumns>
        <FactsColumns>
          <ShowPolicyField
            field={items[4]}
            type="monitor"
            openPolicy={(value) => openPolicy(value, undefined, items[1].value)}
          />
          <ShowPolicyField
            field={items[5]}
            type="rate"
            openPolicy={(value) => openPolicy(value, undefined, items[1].value)}
          />
        </FactsColumns>
      </FactsColumns>
      <FactsRow>
        <AdditionalDetailsStyled
          onClick={() => (showAdditionalDetails = !showAdditionalDetails)}
        >
          <a
            role="button"
            className="collapsed-arrow collapsed btn-link"
            data-toggle="collapse"
            href="#subcriberInformation"
            aria-expanded="true"
            aria-controls="subcriberInformation"
          >
            Additional details
          </a>
        </AdditionalDetailsStyled>
        <div className="fact">{/*placeholder*/}</div>
      </FactsRow>

      <div id="subcriberInformation" className="panel-collapse collapse">
        <FactsColumns>
          <FactsColumns>
            <div>
              <ShowField field={items[1]} />
            </div>
            <div>
              <ShowField field={items[3]} />
            </div>
          </FactsColumns>
          <FactsColumns>
            <div>
              <ShowField />
            </div>
            <div>
              <ShowField />
            </div>
          </FactsColumns>
        </FactsColumns>
        <FactsColumns>
          <FactsColumns>
            <div>
              <ShowField field={items[6]} />
              <ShowField field={items[8]} />
              <ShowField field={items[10]} />
              <ShowField field={items[12]} />
            </div>
            <div>
              <ShowField field={items[7]} />
              <ShowField field={items[9]} />
              <ShowField field={items[11]} />
              {login.isPlatform("gs-kr") ? null : (
                <ShowField field={items[13]} />
              )}
            </div>
          </FactsColumns>
          <FactsColumns>
            <div>
              {login.isPlatform("gs-kr") ? null : (
                <ShowField field={items[14]} />
              )}
              <ShowField field={items[16]} />
              <ShowField field={items[18]} />
              <ShowQuotaField
                field={items[20]}
                ipAddress={items[0].value}
                onLeavingPage={onLeavingPage}
                target={target}
                dpiEnabled={dpiEnabled}
              />
            </div>
            <div>
              {login.isPlatform("gs-kr") ? null : (
                <ShowField field={items[15]} />
              )}
              <ShowField field={items[17]} />
              <ShowField field={items[19]} />
              <ShowSubsGroupField
                field={items[21]}
                ipAddress={items[0].value}
                onLeavingPage={onLeavingPage}
                target={target}
                dpiEnabled={dpiEnabled}
                timePeriod={timePeriod}
              />
            </div>
          </FactsColumns>
        </FactsColumns>
      </div>
    </>
  );
};
ShowFacts.displayName = 'ShowFacts'

const ShowSingleDevice = ({ item, onLeavingPage, target, dpiEnabled, onLoaded, timePeriod, ...settings }) => (
  <ExpandQuotaAndGroupsFacts items={item.facts} onLoaded={onLoaded}>
    {(items) =>
      <ShowFacts
        items={items}
        onLeavingPage={onLeavingPage}
        target={target}
        dpiEnabled={dpiEnabled}
        timePeriod={timePeriod}
        {...settings}
      />}
  </ExpandQuotaAndGroupsFacts>
);

const DevicesSelection = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  .input-group {
    width: auto;
    margin: 0 1.5em;
  }
`;

const parseQuotaAndGroups = (response) => {
  let subsGroups, quota;
  try {
    const lines = response.trim("\n").split("\n");
    for (const line of lines) {
      const [key, value] = line.split(/\:\s+/);
      if (
        (key === "Permanent direct subscriber-groups" ||
          key === "Non-permanent direct subscriber-groups" ||
          key === "Permanent indirect subscriber-groups" ||
          key === "Non-permanent indirect subscriber-groups") &&
        value !== "n/a"
      ) {
        subsGroups += value;
      } else if (key === "Quota state" && value !== "n/a") {
        quota = value;
      }

      if (subsGroups && quota) {
        break;
      }
    }
  } catch (e) {}
  return [
    { label: "Quota", value: quota ? "yes" : "no" },
    { label: "Subscriber groups", value: subsGroups ? "yes" : "no" },
  ];
};

const doSubscriberApiRequest = (ipAddress, bqnId = null) => 
  ipAddress === null
  ? null
  : ifCl.run(`show api subscriber ${ipAddress}${bqnId !== null ? ` bqn-id ${bqnId}` : ''}`)
      .then(parseQuotaAndGroups)
      .catch( error => {
        console.error("show api subscriber error:",error);
        return [
          { label: "Quota", value: "no" },
          { label: "Subscriber groups", value: "no" }
        ];
      })
      .then(extraItems => ({extraItems}));

const ExpandQuotaAndGroupsFacts = ({items=[], children, onLoaded}) => {
  const [request, setRequest] = useState(null);

  const {
    bqnId,
    clusterStatus: { mode },
  } = useContext(BqnContext);

  useEffect(() => {
    if(request){
      request.then(() => onLoaded())
    }
  }, [request, bqnId])

  useEffect( () => {
    const ipAddress = items.length > 0 ? items[0].value : null;
    setRequest(doSubscriberApiRequest(ipAddress, (mode === "cluster" && bqnId !== "all") ? bqnId : null))
  }, [items])

  return request === null
    ? children(items)
    : <Request during={request}>
        {({extraItems}) => children([...items, ...extraItems])}
      </Request>
}

const ShowDevices = ({
  items = [],
  defaultPosition = 0,
  onLeavingPage,
  target,
  dpiEnabled,
  onLoaded,
  timePeriod,
  ...settings
}) => {
  const [position, setPosition] = useState(defaultPosition);

  const options = items.map(({ device }, index) => {
    return { value: index, label: device };
  });

  const handleChange = ({target})=> {
    setPosition(parseInt(target.value));
  }

  return (
    <>
      <DevicesSelection className="input-section">
        <span>Showing IP Address:</span>
        <SelectInputWithIcon
            title="IP Address"
            name="IP Address"
            selected={position}
            onChange={handleChange}
            options={options}
          />
        <span> from a total of {items.length}</span>
      </DevicesSelection>
      <ExpandQuotaAndGroupsFacts items={items[position].facts} onLoaded={onLoaded}>
        {(items) =>
          <ShowFacts
            items={items}
            onLeavingPage={onLeavingPage}
            target={target}
            dpiEnabled={dpiEnabled}
            timePeriod={timePeriod}
            {...settings}
          />}
      </ExpandQuotaAndGroupsFacts>
    </>
  );
};

const typeOfTarget = ({ addr = null, subsId = null }) =>
  addr !== null ? "IP Address" : "Subscriber";

const doRequestFacts = ({target, bqnId = null, mode, ...params}) =>
  target === undefined? null
  : doSubcriberInformationRetrieval(target, bqnId, mode).then((items=null) => ({items, ...params}))


const SubscriberInformation = ({ target, onLeavingPage=doesNothing, setLiveMode, liveMode, dpiEnabled, onLoaded, onBeforeLoad, onIpAddressSelected=doesNothing }) => {
  const [request, setRequest,] = useState(null);
  const actions = useContext(ActionsContext);
  const onLeavingThisPage = React.useCallback(onLeavingPage, []);
  const [ipAddress, setAddress] = useState(target.addr);
  let [searchParams, _] = useSearchParams();

  const {
    bqnId,
    clusterStatus: { mode },
  } = useContext(BqnContext);

  const timePeriod = searchParams.get('timePeriod');

  const doLoad = (loadTarget) =>
    setRequest(doRequestFacts({target: loadTarget, bqnId: (mode === "cluster" && bqnId !== "all") ? bqnId : null, mode}))

  const targetStr = target ? (target.addr ? target.addr : target.subsId) : "";

  useEffect(() => {
    setAddress(target.addr);
    doLoad(target);
  }, [targetStr, bqnId]);

  useEffect(() => {
    onIpAddressSelected(ipAddress);
  }, [ipAddress]);

  const doSubmit = (target) => {
    if((target.addr ? target.addr : target.subsId) === targetStr){
      return;
    }

    onBeforeLoad();
    actions.send("do-load-details", target);
    if (!liveMode) {
      setLiveMode(undefined);
    }
  };
  return (<>
    <SubscriberInformationHeaderDiv className="header">
      <TargetInput target={target} onSubmit={doSubmit} />
      <ShowSubsGroupLink 
        onLeavingPage={onLeavingPage}
        ipAddress={ipAddress}
        target={target}
        dpiEnabled={dpiEnabled}
        timePeriod={timePeriod}/>
      <SubscriberInformationContextMenu doReload={() => doLoad(target)} />
    </SubscriberInformationHeaderDiv>
    {request === null ? null : (
      <Request during={request}>
        { ({items})  =>
          <SubscriberInformationFieldsLayout className="subscriber-info-fields-container">
            {items === null || items.length === 0 ? (
              <NothingToShow targetType={typeOfTarget(target)} onLoaded={onLoaded} target={target}/>
            ) : items.length === 1 ? (
              <ShowSingleDevice
                item={items[0]}
                target={target}
                dpiEnabled={dpiEnabled}
                onLoaded={onLoaded}
                timePeriod={timePeriod}
                onAddressLoaded={setAddress}
                onLeavingPage={onLeavingThisPage}
              />
            ) : (
              <ShowDevices
                items={items}
                onLeavingPage={onLeavingThisPage}
                target={target}
                dpiEnabled={dpiEnabled}
                onLoaded={onLoaded}
                timePeriod={timePeriod}
                onAddressLoaded={setAddress}
              />
            )}
          </SubscriberInformationFieldsLayout>
        }
      </Request>)
    }
  </>);
};

export default SubscriberInformation;
